<template>
    <b-container fluid>
        <b-card>
            <template v-slot:header>
                <h4>Purchases for {{ applicantName }} for AY{{ academicYear }} </h4>
            </template>
            <b-card-body>
                <PurchaseListTable
                    :application-id="applicationId"
                    :purchase-items="purchaseListItems"
                    :cost-type-lookup="costTypeLookup"
                    :can-submit="canSubmit"
                    :can-delete="canDelete"
                    :is-reviewer="isReviewer"
                    :filtered-by="filteredBy"
                    @selected-purchase="navigateToPurchase"
                    @submitted-purchase="onSubmittedPurchase"
                    @deleted-purchase="onDeletedPurchase"
                />
            </b-card-body>
        </b-card>

        <div v-if="showCreateButton" v-show="showCreateButton">
            <b-button variant="primary" @click="newPurchase">
                {{ createPurchaseButtonText }}
            </b-button>
        </div>
    </b-container>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import PurchaseListTable from './PurchaseListTable';

export default {
    name: 'Purchases',

    metaInfo() {
        return { title: 'Purchases' };
    },

    components: {
        PurchaseListTable
    },

    props: {
        applicationId: { type: [String, Number], required: true },
        academicYear: { type: [String, Number], required: true },
        filter: { type: [String, Number], default: null },
    },

    data() {
        return {
        };
    },

    computed: {
        ...mapGetters({
            isAuthorized: 'auth/isAuthorized',
            isReviewer: 'auth/isReviewer',
            ficeName: 'ref/ee/ficeName',
            applicationsById: 'application/applicationsById',
            costTypeLookup: 'ref/application/costTypeLookup',
            costsById: 'cost/costsById',
            proposalsById: 'proposal/proposalsById',
            currentAy: 'ref/global/currentAy'
        }),

        ...mapState('purchase', {
            purchaseList: 'applicationPurchases',
        }),

        filteredBy() {
            return this.$route.query.filter || this.filter;
        },

        purchaseListItems() {
            return this.purchaseList.map((purchase) => {
                return {
                    ...purchase,
                    // FIXME: checking if defined is a hack. We need to
                    // figure out why this is not working when we navigate
                    // here from an inbox, then "back to purchases"
                    proposalTitle: (this.proposalsById[purchase.proposal_id])
                        ? this.proposalsById[purchase.proposal_id].content.proposal_title
                        : '',
                    costDescription: (this.costsById[purchase.cost_id])
                        ? this.costsById[purchase.cost_id].description
                        : '',
                    costType: (this.costsById[purchase.cost_id])
                        ? this.costsById[purchase.cost_id].cost_type
                        : '',
                    _actions: {
                        submit: purchase.state ? this.isAuthorized('submit', purchase) : false,
                        delete: purchase.state ? this.isAuthorized('delete', purchase) : false,
                    },
                };
            });
        },

        applicantName() {
            return this.applicationId && this.applicationsById[this.applicationId]
                ? this.ficeName(this.applicationsById[this.applicationId].applicant_id)
                : 'Institution';
        },

        showCreateButton() {
            // return this.isAuthorized('create', 'purchase');
            // Temporarily disabling the add button here
            return false;
        },

        createPurchaseButtonText() {
            return 'Add a Purchase';
        },

    },

    async created() {
        await this.fetchApplicationList();
        const applicationAcademicYear = this.applicationsById && this.applicationsById[this.applicationId]
            ? this.applicationsById[this.applicationId].academic_year
            : this.currentAy;
        await this.fetchRefApplication(applicationAcademicYear);
        await this.fetchRefEe(applicationAcademicYear);
        await this.fetchApplicationCosts(this.applicationId);
        await this.fetchApplicationProposals(this.applicationId);

        this.fetchApplicationPurchases(this.applicationId);
    },

    methods: {
        ...mapActions({
            createPurchase: 'purchase/create',
            submitPurchase: 'purchase/submit',
            deletePurchase: 'purchase/delete',
            fetchRefApplication: 'ref/application/fetchRefApplication',
            fetchRefEe: 'ref/ee/fetchRefEe',
            fetchApplicationList: 'application/fetchApplicationList',
            fetchApplicationProposals: 'proposal/fetchProposalList',
            fetchApplicationPurchases: 'purchase/fetchApplicationPurchases',
            fetchApplicationCosts: 'cost/fetchApplicationCosts',
            // resetPurchaseState: 'purchase/resetPurchaseState',
            // resetCostState: 'purchase/resetCostState',
        }),

        canDelete(purchase) {
            return this.isAuthorized('delete', purchase);
        },

        canSubmit(purchase) {
            return this.isAuthorized('submit', purchase);
        },

        navigateToPurchase(row) {
            this.$router.push({
                name: 'purchase.History',
                params: {
                    applicationId: this.applicationId,
                    proposalId: row.proposal_id,
                    costId: row.cost_id,
                    purchaseId: row.purchase_id
                },
            });
        },

        onSubmittedPurchase(row) {
            const applicationId = this.applicationId;
            const proposalId = row.item.proposal_id;
            const costId = row.item.cost_id;
            const purchaseId = row.item.purchase_id;

            this.submitPurchase({ applicationId, proposalId, costId, purchaseId });
        },

        onDeletedPurchase(row) {
            const applicationId = this.applicationId;
            const proposalId = row.item.proposal_id;
            const costId = row.item.cost_id;
            const purchaseId = row.item.purchase_id;

            this.deletePurchase({ applicationId, proposalId, costId, purchaseId });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '$StyleVars';

h4 {
    text-align: center;
}

h6 {
    text-align: center;
}

td.table-success {
    vertical-align: middle;
    text-align: center;
}

table#list-of-local-applications {
    vertical-align: middle;
    text-align: center;
}

#list-of-local-applications__caption_ {
    font-size: .75rem;
}

tr {
    cursor: pointer;
}

$action-button-width: 44.47px;
$estimated-description-column-width: calc(
    83vw - (11ch + 8ch + 22ch + (#{$action-button-width * 3}) + (16px * 3))
);

.table {
    .actions-column .col {
        padding-left: 0;
        padding-right: 0;
        white-space: nowrap;

        .action-placeholder {
            width: $action-button-width;
        }
    }

    /deep/ thead {
        th.description {
            width: calc(100% - (50px + 50px + 80px + 125px));
        }

        th.actions-header,
        th.actions-header-1,
        th.actions-header-2,
        th.actions-header-3,
        th.actions-header-4 {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            white-space: nowrap;

            .col {
                padding: 5px 9px;

                svg {
                    vertical-align: bottom;
                }
            }
        }

        th.actions-header-1 {
            width: $action-button-width * 1;
        }

        th.actions-header-2 {
            width: $action-button-width * 2;
        }

        th.actions-header-3 {
            width: $action-button-width * 3;
        }

        th.actions-header-4 {
            width: $action-button-width * 4;
        }
    }

    /deep/ tbody {
        tr:hover {
            cursor: pointer;
        }

        td {
            white-space: nowrap;

            .description {
                position: relative;
                width: $estimated-description-column-width;

                &:before {
                    content: attr(title);
                    max-width: 100%;
                    overflow: hidden;
                    position: absolute;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                &:after {
                    content: attr(title);
                    display: inline-block;
                    height: 0;
                    overflow: hidden;
                }
            }
        }
    }
}

</style>
